import { defineMessages, MessageDescriptor } from 'react-intl';
import { CalloutStatus } from 'components/Callout';

/**
 * Source of truth for the 7 legal sections (TZ-376). Copy lives here as
 * `defineMessages` descriptors (extracted to en.json; Arabic in ar.json) and the
 * page renders straight from the `LEGAL_SECTIONS` array — no per-section markup.
 *
 * `<b>…</b>` segments are rendered bold via the rich-text handler in the page.
 */
const m = defineMessages({
  s1Title: { id: 'terms.section.01.title', defaultMessage: 'Terms and Conditions' },
  s1p1: {
    id: 'terms.section.01.p1',
    defaultMessage:
      'Tickets purchased through Ticket Zone or its agents are non-refundable except in the specific circumstances set out in the Refund Policy. Tickets may only be exchanged in limited cases and cannot be transferred to a third party, including family or friends.',
  },
  s1c1: {
    id: 'terms.section.01.callout1',
    defaultMessage:
      'Tickets may not be resold for more than their original purchase price. Any attempt to do so will result in the ticket being cancelled with no refund, and the ticket will no longer be valid for entry.',
  },
  s1c2: {
    id: 'terms.section.01.callout2',
    defaultMessage:
      'We may ask you to prove you are the original buyer at the entry point — for example a national ID, passport, or driving licence. The name must match the name on the ticket.',
  },
  s1c3: {
    id: 'terms.section.01.callout3',
    defaultMessage:
      'Protecting your tickets from loss or theft is your responsibility. Ticket Zone is not responsible for replacing lost tickets.',
  },

  s2Title: { id: 'terms.section.02.title', defaultMessage: 'Using Tickets & Attendance' },
  s2p1: {
    id: 'terms.section.02.p1',
    defaultMessage:
      'It is your responsibility to check entry restrictions before buying a ticket (such as age or dress-code rules). Ticket Zone is not responsible for a refund if entry is denied for not meeting these restrictions.',
  },
  s2p2: {
    id: 'terms.section.02.p2',
    defaultMessage:
      'Ticket Zone is not responsible for providing parking at events, nor for anyone appearing in promotional photos and videos of the event.',
  },
  s2c1: {
    id: 'terms.section.02.callout1',
    defaultMessage:
      'Latecomers are only admitted at a suitable break and may be seated in alternative seats to avoid disrupting the performance.',
  },
  s2c2: {
    id: 'terms.section.02.callout2',
    defaultMessage:
      'Venue management may confiscate prohibited items (food, drinks, video cameras, selfie sticks, etc.). Ticket Zone is not responsible for returning confiscated items.',
  },

  s3Title: { id: 'terms.section.03.title', defaultMessage: 'Ticket Delivery' },
  s3p1: {
    id: 'terms.section.03.p1',
    defaultMessage:
      'Tickets are sent to you by WhatsApp message to your personal number. You can print them or use them digitally. The QR code usually appears about 6 hours before the event starts.',
  },
  s3c1: {
    id: 'terms.section.03.callout1',
    defaultMessage:
      'Ticket Zone may refuse any ticket that does not carry a valid QR code, with no refund.',
  },

  s4Title: { id: 'terms.section.04.title', defaultMessage: 'Refund Policy' },
  s4p1: {
    id: 'terms.section.04.p1',
    defaultMessage:
      'Ticket Zone sells tickets on behalf of organizers. <b>The ticket value is refunded only in the following cases:</b>',
  },
  s4cond1: {
    id: 'terms.section.04.cond1',
    defaultMessage: 'If the event is cancelled for any reason.',
  },
  s4cond2: {
    id: 'terms.section.04.cond2',
    defaultMessage: 'If the headline artist cancels or is unable to perform.',
  },
  s4cond3: {
    id: 'terms.section.04.cond3',
    defaultMessage: 'If the event is moved to a different date.',
  },
  s4cond4: {
    id: 'terms.section.04.cond4',
    defaultMessage: 'If the start or end of the event shifts by more than 4 hours.',
  },
  s4cond5: {
    id: 'terms.section.04.cond5',
    defaultMessage: 'If the event is postponed for any reason.',
  },
  s4cond6: {
    id: 'terms.section.04.cond6',
    defaultMessage:
      'If the venue changes to a substantially different location (40 minutes or more by car).',
  },
  s4method: {
    id: 'terms.section.04.method',
    defaultMessage:
      '<b>Refund method:</b> the amount is refunded by the original payment method (to the same card used) within 7–14 business days from the date the refund request is approved.',
  },
  s4p2: {
    id: 'terms.section.04.p2',
    defaultMessage:
      'Minor changes to performance times, the quality of the performance, or technical issues are not valid grounds for a refund.',
  },

  s5Title: { id: 'terms.section.05.title', defaultMessage: 'Liability' },
  s5p1: {
    id: 'terms.section.05.p1',
    defaultMessage:
      'Ticket Zone is not responsible for any problems, damages, or injuries arising from the event for which the customer purchased tickets.',
  },

  s6Title: { id: 'terms.section.06.title', defaultMessage: 'Privacy Policy' },
  s6p1: {
    id: 'terms.section.06.p1',
    defaultMessage:
      'We take our customers’ privacy seriously. Protecting your data is a matter of trust, and we will not share your data with any third party without your permission.',
  },
  s6DataLabel: { id: 'terms.section.06.dataLabel', defaultMessage: 'The information we collect:' },
  s6Data1: { id: 'terms.section.06.data1', defaultMessage: 'Full (three-part) name' },
  s6Data2: {
    id: 'terms.section.06.data2',
    defaultMessage: 'A mobile number that works on WhatsApp',
  },
  s6Data3: { id: 'terms.section.06.data3', defaultMessage: 'Email address' },
  s6p2: {
    id: 'terms.section.06.p2',
    defaultMessage:
      'We use this information only for the purposes set out in the Privacy Policy. The information must be accurate and not misleading, and we reserve the right to request documents to verify it.',
  },

  s7Title: { id: 'terms.section.07.title', defaultMessage: 'Amending the Terms & Conditions' },
  s7p1: {
    id: 'terms.section.07.p1',
    defaultMessage:
      'Ticket Zone reserves the right to amend or update these Terms and Conditions at any time without prior notice. Updates will appear on this page with the last-updated date at the top.',
  },
});

/** An ordered piece of a section body: a paragraph or a bulleted list. */
export type LegalBlock =
  | { kind: 'paragraph'; text: MessageDescriptor }
  | { kind: 'list'; label?: MessageDescriptor; items: MessageDescriptor[] };

export interface LegalCallout {
  status: CalloutStatus;
  text: MessageDescriptor;
}

export interface LegalSectionData {
  number: number;
  title: MessageDescriptor;
  body: LegalBlock[];
  callouts?: LegalCallout[];
}

export const LEGAL_SECTIONS: LegalSectionData[] = [
  {
    number: 1,
    title: m.s1Title,
    body: [{ kind: 'paragraph', text: m.s1p1 }],
    callouts: [
      { status: 'warning', text: m.s1c1 },
      { status: 'info', text: m.s1c2 },
      { status: 'info', text: m.s1c3 },
    ],
  },
  {
    number: 2,
    title: m.s2Title,
    body: [
      { kind: 'paragraph', text: m.s2p1 },
      { kind: 'paragraph', text: m.s2p2 },
    ],
    callouts: [
      { status: 'info', text: m.s2c1 },
      { status: 'warning', text: m.s2c2 },
    ],
  },
  {
    number: 3,
    title: m.s3Title,
    body: [{ kind: 'paragraph', text: m.s3p1 }],
    callouts: [{ status: 'warning', text: m.s3c1 }],
  },
  {
    number: 4,
    title: m.s4Title,
    body: [
      { kind: 'paragraph', text: m.s4p1 },
      {
        kind: 'list',
        items: [m.s4cond1, m.s4cond2, m.s4cond3, m.s4cond4, m.s4cond5, m.s4cond6],
      },
      { kind: 'paragraph', text: m.s4p2 },
    ],
    callouts: [{ status: 'info', text: m.s4method }],
  },
  {
    number: 5,
    title: m.s5Title,
    body: [{ kind: 'paragraph', text: m.s5p1 }],
  },
  {
    number: 6,
    title: m.s6Title,
    body: [
      { kind: 'paragraph', text: m.s6p1 },
      { kind: 'list', label: m.s6DataLabel, items: [m.s6Data1, m.s6Data2, m.s6Data3] },
      { kind: 'paragraph', text: m.s6p2 },
    ],
  },
  {
    number: 7,
    title: m.s7Title,
    body: [{ kind: 'paragraph', text: m.s7p1 }],
  },
];
